export default defineNuxtPlugin((nuxtApp) => {
    let observer: IntersectionObserver | null = null;
    let allRevealElements: HTMLElement[] = [];
    let scrollContainers: (Window | Element)[] = [];
    let isTicking = false;

    const updateVisibility = () => {
        if (!process.client || allRevealElements.length === 0) return;
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

        allRevealElements.forEach((el) => {
            if (!el || !el.isConnected) return;
            const rect = el.getBoundingClientRect();
            // 只要元素一部分进入视口（距视口底端 20px 以上，且没有完全滚出视口顶端）
            if (rect.top <= viewportHeight - 20 && rect.bottom >= 20) {
                el.classList.add('is_visible');
            } else if (rect.bottom < 0 || rect.top > viewportHeight) {
                // 离开视口后移除，参考首页，每次滚动进出都有动效
                el.classList.remove('is_visible');
            }
        });
    };

    const handleScroll = () => {
        if (isTicking) return;
        isTicking = true;
        requestAnimationFrame(() => {
            updateVisibility();
            isTicking = false;
        });
    };

    const setupObserver = () => {
        if (!process.client) return;

        // 清理旧 observer 与事件监听
        if (observer) {
            observer.disconnect();
            observer = null;
        }
        scrollContainers.forEach((target) => {
            target.removeEventListener('scroll', handleScroll as EventListener);
        });
        scrollContainers = [];

        const selectorStr = [
            // Company 页面关键模块与卡片（不含底部 Our Mission）
            '.company_vision',
            '.teams_box .teams_top',
            '.teams_box .team_wrap_ul .per_lis',
            '.makes_unique .makes_top',
            '.makes_unique .maker_tab .mk_wrap',
            
            // Research 页面关键模块与卡片
            '.research_first',
            '.research_top',
            '.research_second',
            '.research_unique .title',
            '.research_unique .maker_tab .mk_wrap',
            
            // Podcast 页面关键模块与卡片
            '.podcast-intro',
            '.podcast-recent .teams_top',
            '.recent-episode-card',
            '.podcast-page .teams_box .teams_top',
            '.podcast-page .team_wrap_ul .per_lis',
            
            // News Detail 页面底部推荐
            '.news_detail .about_contact_card',
            '.news_detail .featured_news_section',
            
            // News List 页面
            '.news_content_box .news_filter_section',
            '.news_content_box .mk_wrap'
        ].join(', ');

        const elements = Array.from(document.querySelectorAll<HTMLElement>(selectorStr));
        if (elements.length === 0) return;

        allRevealElements = elements;
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

        observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const target = entry.target as HTMLElement;
                    if (entry.isIntersecting) {
                        target.classList.add('is_visible');
                    } else {
                        // 离开视口时移除 class，再次滚入时重新播放动效（与首页保持一致）
                        target.classList.remove('is_visible');
                    }
                });
            },
            {
                root: null, // 监听浏览器视口，完美适配所有滚动容器与页面
                threshold: 0.06,
                rootMargin: '0px 0px -10px 0px'
            }
        );

        elements.forEach((el) => {
            el.classList.add('scroll_reveal');
            
            // 首屏已在视口内的元素立即展现
            const rect = el.getBoundingClientRect();
            if (rect.top < viewportHeight - 30 && rect.bottom > 20) {
                el.classList.add('is_visible');
            } else {
                el.classList.remove('is_visible');
            }

            observer?.observe(el);
        });

        // 监听 window 和所有可能的滚动容器作为双重保障
        scrollContainers = [window];
        const domScrolls = document.querySelectorAll('.home_wrap, .hopeai_home, .podcast-page');
        domScrolls.forEach((container) => {
            scrollContainers.push(container);
        });

        scrollContainers.forEach((target) => {
            target.addEventListener('scroll', handleScroll as EventListener, { passive: true });
        });
    };

    // 路由切换和页面渲染完成后初始化
    nuxtApp.hook('page:finish', () => {
        setTimeout(setupObserver, 80);
        setTimeout(setupObserver, 350);
    });

    nuxtApp.$router?.afterEach(() => {
        setTimeout(setupObserver, 100);
        setTimeout(setupObserver, 400);
    });

    if (process.client) {
        if (document.readyState === 'complete') {
            setTimeout(setupObserver, 80);
        } else {
            window.addEventListener('load', () => {
                setTimeout(setupObserver, 80);
            });
            setTimeout(setupObserver, 300);
        }
    }
});
